import React from 'react';
import { motion } from 'motion/react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent, BirthdayTimelineItem } from '../content';
import { SectionBgImage, hasSectionBg } from './SectionBgImage';

function TimelineEntry({
  item,
  index,
  isLast,
}: {
  item: BirthdayTimelineItem;
  index: number;
  isLast: boolean;
}) {
  const isEven = index % 2 === 0;

  return (
    <div className="relative flex items-start gap-6">
      {/* vertical connector */}
      {!isLast && (
        <div className="absolute left-6 top-14 w-0.5 h-full bg-gradient-to-b from-blue-400/60 to-blue-800/10" />
      )}

      {/* node */}
      <div className="relative z-10 flex-shrink-0">
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 300, damping: 20, delay: index * 0.1 }}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 flex items-center justify-center text-lg shadow-lg border-2 border-blue-300/30"
        >
          🌟
        </motion.div>
        <motion.div
          animate={{ scale: [1, 1.5, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.3 }}
          className="absolute inset-0 rounded-full bg-blue-400/30"
        />
      </div>

      {/* card */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? -30 : 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.65, delay: index * 0.1, ease: BDAY_EASE }}
        className="flex-1 mb-10"
      >
        <div className="bg-white/5 backdrop-blur-sm border border-blue-400/20 rounded-2xl overflow-hidden shadow-lg shadow-blue-900/20">
          {item.imageUrl && (
            <div className="h-44 overflow-hidden">
              <img
                src={item.imageUrl}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          )}
          <div className="p-5">
            <h3 className="text-white font-bold text-lg mb-1">{item.title}</h3>
            {item.description && (
              <p className="text-blue-200 text-sm leading-relaxed">{item.description}</p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function MemoryTimelineSection({ content }: { content: BirthdayContent }) {
  const items = content.timelineItems;
  if (!items.length) return null;

  const bg = content.sectionBackgrounds.timeline;
  const active = hasSectionBg(bg);

  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={active ? undefined : { background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 100%)' }}
    >
      <SectionBgImage bg={bg} />
      <div className="max-w-2xl mx-auto">
        {/* heading */}
        <div className="text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            🌟 The Journey
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            Memory Timeline
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-blue-300 text-sm"
          >
            A journey through beautiful moments
          </motion.p>
        </div>

        {/* timeline */}
        <div className="relative flex flex-col gap-0">
          {items.map((item, i) => (
            <TimelineEntry key={i} item={item} index={i} isLast={i === items.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
